import { formatFigure } from './format';
import { SliceCell, TaskWorkspace, WorkspaceAnswers, WorkspaceColumn } from './types';

export const coordinateKey = (coordinates: Record<string, string>): string =>
  Object.keys(coordinates)
    .sort()
    .map((key) => `${key}=${coordinates[key]}`)
    .join('|');

export const rowLabel = (coordinates: Record<string, string>): string => {
  const values = Object.keys(coordinates)
    .sort()
    .map((key) => coordinates[key])
    .filter(Boolean);
  return values.join(' · ') || 'Plan';
};

export interface SheetRow {
  key: string;
  label: string;
  coordinates: Record<string, string>;
}

export const cellsOf = (workspace: TaskWorkspace): SliceCell[] => {
  if (!workspace.view) return [];
  if (workspace.view.source === 'snapshot') return workspace.view.payload.cells ?? [];
  return workspace.view.slice?.cells ?? [];
};

export const answersOf = (workspace: TaskWorkspace): WorkspaceAnswers => {
  if (!workspace.view) {
    return {
      flags: workspace.workspace_state?.flags ?? {},
      commentary: workspace.workspace_state?.commentary ?? '',
      assumptions: workspace.workspace_state?.assumptions ?? '',
    };
  }
  if (workspace.view.source === 'snapshot') {
    return {
      flags: workspace.view.payload.flags ?? {},
      commentary: workspace.view.payload.commentary ?? '',
      assumptions: workspace.view.payload.assumptions ?? '',
    };
  }
  return {
    flags: workspace.view.workspace_state?.flags ?? {},
    commentary: workspace.view.workspace_state?.commentary ?? '',
    assumptions: workspace.view.workspace_state?.assumptions ?? '',
  };
};

const matchesGrid = (cell: SliceCell, blockId: number | null | undefined, names: Set<string>) => {
  if (!names.has(cell.indicator_or_column)) return false;
  if (blockId == null || cell.block_id == null) return true;
  return cell.block_id === blockId;
};

export const rowsForGrid = (
  cells: SliceCell[],
  blockId: number | null | undefined,
  columns: WorkspaceColumn[],
): SheetRow[] => {
  const names = new Set(columns.map((column) => column.indicator_or_column));
  const seen = new Map<string, Record<string, string>>();
  cells.forEach((cell) => {
    if (!matchesGrid(cell, blockId, names)) return;
    const key = coordinateKey(cell.coordinates || {});
    if (!seen.has(key)) seen.set(key, cell.coordinates || {});
  });
  return Array.from(seen.entries())
    .map(([key, coordinates]) => ({ key, label: rowLabel(coordinates), coordinates }))
    .sort((left, right) => left.label.localeCompare(right.label));
};

export const findCell = (
  cells: SliceCell[],
  blockId: number | null | undefined,
  indicator: string,
  coordinates: Record<string, string>,
): SliceCell | undefined => {
  const key = coordinateKey(coordinates);
  return cells.find(
    (cell) =>
      cell.indicator_or_column === indicator &&
      coordinateKey(cell.coordinates || {}) === key &&
      (blockId == null || cell.block_id == null || cell.block_id === blockId),
  );
};

export const displayCell = (cell: SliceCell | undefined): string => {
  if (!cell) return '';
  if (cell.display != null && cell.display !== '') return cell.display;
  if (cell.value == null) return '';
  return String(cell.value);
};

const periodNumbers = (cell: SliceCell): number[] => {
  const { periods } = cell;
  if (!periods) return [];
  return Object.values(periods)
    .filter((value) => value != null && value !== '')
    .map((value) => (typeof value === 'number' ? value : Number(value)))
    .filter((value) => !Number.isNaN(value));
};

/** One figure for a metric or summary line. Several rows, and their periods, are added. */
export const figureFor = (cells: SliceCell[], indicator: string | null | undefined): string => {
  if (!indicator) return '';
  const matches = cells.filter((cell) => cell.indicator_or_column === indicator);
  if (matches.length === 0) return '';
  const fromPeriods = matches.flatMap((cell) => periodNumbers(cell));
  if (fromPeriods.length > 0) {
    return formatFigure(fromPeriods.reduce((total, value) => total + value, 0));
  }
  if (matches.length === 1) return displayCell(matches[0]);
  const present = matches.filter((cell) => cell.value != null && cell.value !== '');
  const numbers = present.map((cell) =>
    typeof cell.value === 'number' ? cell.value : Number(cell.value),
  );
  if (present.length > 0 && numbers.every((value) => !Number.isNaN(value))) {
    return formatFigure(numbers.reduce((total, value) => total + value, 0));
  }
  return matches
    .map((cell) => displayCell(cell))
    .filter(Boolean)
    .join(', ');
};

export const hasTimeColumns = (cells: SliceCell[]): boolean =>
  cells.some((cell) => cell.periods && Object.keys(cell.periods).length > 0);

/** Period labels in the order the engine returned them. */
export const periodLabels = (cells: SliceCell[]): string[] => {
  const labels: string[] = [];
  cells.forEach((cell) => {
    Object.keys(cell.periods || {}).forEach((label) => {
      if (!labels.includes(label)) labels.push(label);
    });
  });
  return labels;
};

export const constantText = (
  constant: number | string | null | undefined,
  display?: string | null,
) => {
  if (display != null && display !== '') return display;
  if (constant == null) return '';
  return String(constant);
};
